import { StatusChipColors } from 'components/darwin-ui';
import { TFunction } from 'i18next';

/**
 * Process Lifecycle Status Enum
 * Matches the backend ProcessLifeCycleStatus enum
 */
export enum ProcessLifeCycleStatus {
    INACTIVE = 'inactive',
    TEST = 'test',
    PRODUCTION = 'production',
}

/**
 * Translation keys for process lifecycle statuses
 * Maps to process-map.columns translation namespace
 */
export const PROCESS_STATUS_TRANSLATION_KEYS: Record<
    ProcessLifeCycleStatus,
    string
> = {
    [ProcessLifeCycleStatus.INACTIVE]: 'process-map.columns.inactive',
    [ProcessLifeCycleStatus.TEST]: 'process-map.columns.test',
    [ProcessLifeCycleStatus.PRODUCTION]: 'process-map.columns.deploy',
};

/**
 * Status configuration including color mapping
 */
export interface ProcessStatusConfig {
    value: ProcessLifeCycleStatus;
    translationKey: string;
    color: StatusChipColors;
}

export const PROCESS_STATUS_CONFIG: Record<
    ProcessLifeCycleStatus,
    ProcessStatusConfig
> = {
    [ProcessLifeCycleStatus.INACTIVE]: {
        value: ProcessLifeCycleStatus.INACTIVE,
        translationKey: PROCESS_STATUS_TRANSLATION_KEYS[ProcessLifeCycleStatus.INACTIVE],
        color: StatusChipColors.red,
    },
    [ProcessLifeCycleStatus.TEST]: {
        value: ProcessLifeCycleStatus.TEST,
        translationKey: PROCESS_STATUS_TRANSLATION_KEYS[ProcessLifeCycleStatus.TEST],
        color: StatusChipColors.yellow,
    },
    [ProcessLifeCycleStatus.PRODUCTION]: {
        value: ProcessLifeCycleStatus.PRODUCTION,
        translationKey: PROCESS_STATUS_TRANSLATION_KEYS[ProcessLifeCycleStatus.PRODUCTION],
        color: StatusChipColors.green,
    },
};

/**
 * Array of all process statuses for iteration
 */
export const PROCESS_STATUSES = Object.values(ProcessLifeCycleStatus);

/**
 * Get the color for a given process status
 */
export function getProcessStatusColor(
    status: ProcessLifeCycleStatus
): StatusChipColors {
    return PROCESS_STATUS_CONFIG[status].color;
}

/**
 * Get the translated label for a given process status
 */
export function getProcessStatusLabel(
    status: ProcessLifeCycleStatus,
    t: TFunction
): string {
    return t(PROCESS_STATUS_TRANSLATION_KEYS[status]);
}

/**
 * Get the status configuration for a given status value
 */
export function getProcessStatusConfig(
    status: ProcessLifeCycleStatus
): ProcessStatusConfig {
    return PROCESS_STATUS_CONFIG[status];
}
